import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Trash2, Edit, Send, Copy, Check, X, Users, Link } from 'lucide-react';
import { toast } from 'sonner';

const priceForCount = (count) => (count <= 1 ? 69 : count <= 5 ? 102 : 164);
const PRICE_TABLE = Array.from({ length: 10 }, (_, i) => ({ count: i + 1, price: priceForCount(i + 1) }));

const emptyForm = { nome: '', whatsapp: '', login: '', password: '' };

export default function Employees() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const portalUrl = `${window.location.origin}/portal/${user?.id}`;

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/employees/');
      setEmployees(res.data);
    } catch { toast.error('Erro ao carregar funcionários'); }
    finally { setLoading(false); }
  };

  const currentPrice = priceForCount(employees.filter(e => e.is_active).length);

  const save = async () => {
    if (!form.nome || !form.login) { toast.error('Nome e login são obrigatórios'); return; }
    if (!editId && !form.password) { toast.error('Senha é obrigatória para novo funcionário'); return; }
    setSaving(true);
    try {
      if (editId) {
        const payload = { nome: form.nome, whatsapp: form.whatsapp, login: form.login };
        if (form.password) payload.password = form.password;
        await api.put(`/employees/${editId}`, payload);
        toast.success('Funcionário atualizado!');
      } else {
        await api.post('/employees/', form);
        toast.success('Funcionário cadastrado!');
      }
      setShowForm(false); setEditId(null); setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir funcionário?')) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success('Funcionário excluído');
      load();
    } catch { toast.error('Erro ao excluir'); }
  };

  const startEdit = (e) => {
    setEditId(e.id);
    setForm({ nome: e.nome, whatsapp: e.whatsapp || '', login: e.login, password: '' });
    setShowForm(true);
  };

  const copyPortalLink = () => {
    navigator.clipboard?.writeText(portalUrl);
    setLinkCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const sendWhatsApp = (emp) => {
    const wpp = (emp.whatsapp || '').replace(/\D/g, '');
    if (!wpp) { toast.error('Funcionário sem WhatsApp cadastrado'); return; }
    const msg = `Olá ${emp.nome}! Aqui está o seu link de acesso ao sistema: ${portalUrl}`;
    window.open(`https://wa.me/${wpp}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const toggleActive = async (emp) => {
    await api.put(`/employees/${emp.id}`, { is_active: !emp.is_active });
    toast.success(emp.is_active ? 'Funcionário desativado' : 'Funcionário ativado');
    load();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-7 h-7 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in pb-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Funcionários</h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            {employees.filter(e => e.is_active).length} ativo(s) · <span className="text-orange-400 font-medium">R${currentPrice},00/mês</span>
          </p>
        </div>
        <button
          data-testid="new-employee-btn"
          onClick={() => { setShowForm(true); setEditId(null); setForm(emptyForm); }}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold px-4 py-2 rounded-xl transition-colors text-sm"
        >
          <Plus size={16} />
          Novo funcionário
        </button>
      </div>

      {/* Portal link card */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
            <Link size={16} className="text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white">Link do Portal de Funcionários</div>
            <div className="text-xs text-zinc-500 truncate mt-0.5">{portalUrl}</div>
          </div>
          <button
            data-testid="copy-portal-link-btn"
            onClick={copyPortalLink}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
              linkCopied ? 'border-green-500/30 bg-green-500/10 text-green-400' : 'border-zinc-700 text-zinc-400 hover:text-white'
            }`}
          >
            {linkCopied ? <Check size={13} /> : <Copy size={13} />}
            {linkCopied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
      </div>

      {/* Price table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-medium text-zinc-400 mb-3">Tabela de preços por funcionário</h3>
        <div className="flex flex-wrap gap-2">
          {PRICE_TABLE.map(({ count, price }) => (
            <div key={count} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              employees.filter(e => e.is_active).length === count
                ? 'border-orange-500 bg-orange-500/10 text-orange-400'
                : 'border-zinc-800 text-zinc-500'
            }`}>
              {count} func = R${price}
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-zinc-900 border border-orange-500/30 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-white">{editId ? 'Editar funcionário' : 'Novo funcionário'}</h2>
            <button onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }} className="text-zinc-500 hover:text-white">
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="field-label">Nome *</label>
              <input className="field-input" value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Nome completo" />
            </div>
            <div>
              <label className="field-label">WhatsApp</label>
              <input className="field-input" value={form.whatsapp} onChange={e => setForm(p => ({ ...p, whatsapp: e.target.value }))} placeholder="(99) 99999-9999" />
            </div>
            <div>
              <label className="field-label">Login *</label>
              <input className="field-input" value={form.login} onChange={e => setForm(p => ({ ...p, login: e.target.value }))} placeholder="usuario@empresa" />
            </div>
            <div className="col-span-2">
              <label className="field-label">{editId ? 'Nova senha (deixe em branco para manter)' : 'Senha *'}</label>
              <input type="password" className="field-input" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Mínimo 6 caracteres" />
            </div>
          </div>
          <div className="flex gap-3 justify-center">
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 text-sm border border-zinc-700 text-zinc-400 hover:text-white rounded-xl">
              Cancelar
            </button>
            <button
              data-testid="save-employee-btn"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-semibold px-6 py-2 rounded-xl text-sm transition-colors"
            >
              <Check size={15} />
              {saving ? 'Salvando...' : 'Salvar funcionário'}
            </button>
          </div>
        </div>
      )}

      {/* Employee list */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h2 className="font-heading font-semibold text-white">Lista de funcionários ({employees.length})</h2>
        </div>
        {employees.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <Users size={32} className="text-zinc-700 mx-auto" />
            <p className="text-zinc-500 text-sm">Nenhum funcionário cadastrado</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/50">
            {employees.map(emp => (
              <div key={emp.id} data-testid={`employee-item-${emp.id}`} className="flex items-center gap-3 px-5 py-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${emp.is_active ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-700 text-zinc-500'}`}>
                  {emp.nome[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white text-sm">{emp.nome}</div>
                  <div className="text-xs text-zinc-500">{emp.login}{emp.whatsapp ? ` · ${emp.whatsapp}` : ''}</div>
                </div>
                <span className={`badge text-xs ${emp.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-500'}`}>
                  {emp.is_active ? 'Ativo' : 'Inativo'}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => sendWhatsApp(emp)} title="Enviar link via WhatsApp"
                    className="p-1.5 text-zinc-500 hover:text-green-400 transition-colors">
                    <Send size={14} />
                  </button>
                  <button onClick={() => toggleActive(emp)} title={emp.is_active ? 'Desativar' : 'Ativar'}
                    className="p-1.5 text-zinc-500 hover:text-yellow-400 transition-colors">
                    {emp.is_active ? <X size={14} /> : <Check size={14} />}
                  </button>
                  <button onClick={() => startEdit(emp)} title="Editar"
                    className="p-1.5 text-zinc-500 hover:text-orange-400 transition-colors">
                    <Edit size={14} />
                  </button>
                  <button onClick={() => remove(emp.id)} title="Excluir"
                    className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
